import { CommonModule } from '@angular/common'
import { HttpResponse } from '@angular/common/http'
import { Component, ElementRef, inject, Input, OnInit, Renderer2, ViewChild } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { ActivatedRoute } from '@angular/router'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { IDownload, UploadedFile } from '@app/modules/event-landing-page/model/download.model'
import { EventLandingService } from '@app/modules/event-landing-page/services/event-landing.service'
import { ServerRepresentedEvent } from '@app/modules/events/models/event.model'
import { LandingPageOptions } from '@app/modules/personas/interfaces/persona-landing-option.interface'
import { DragNDropDirective } from '@app/modules/shared/directives/drag-n-drop.directive'
import { FileSizePipe } from '@app/modules/shared/pipes/file-size.pipe'
import { ToastrService } from '@app/services/toastr.service'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { CarouselModule, OwlOptions } from 'ngx-owl-carousel-o'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

@Component({
  selector: 'ngx-upload-input',
  templateUrl: './upload-input.component.html',
  styleUrls: ['./upload-input.component.scss'],
  imports: [CommonModule, CarouselModule, MatButtonModule, MatIconModule, MatProgressBarModule, FileSizePipe, DragNDropDirective, TranslateModule]
})
export class UploadInputComponent implements OnInit {
  @ViewChild('uploadInput', { static: false }) uploadInput: ElementRef<HTMLInputElement>
  @ViewChild('uploadContainer', { static: false }) uploadContainer: ElementRef<HTMLDivElement>
  @Input() event: ServerRepresentedEvent
  @Input() options: LandingPageOptions
  @Input() showDownloads: boolean = true
  @Input() customLabel: string = 'Downloads'
  @Input() helpText: string = 'You can add PDF files and photos'
  @Input() label: string = ''
  customOptions: OwlOptions = {
    loop: false,
    mouseDrag: true,
    touchDrag: true,
    pullDrag: true,
    dots: false,
    navSpeed: 200,
    nav: true,
    margin: 30,
    items: 7,
    responsive: {
      0: {
        items: 7
      }
    },
    navText: ['<i class="fa fa-chevron-left"></i>', '<i class="fa fa-chevron-right"></i>']
  }
  translate = inject(TranslateService)

  persona_id: number = this.activatedRoute.snapshot.params.persona_id
  uploadStatus = this.translate.instant('v2.fields.upload')
  isUploading = false
  files: IDownload[] = []
  uploadedFiles: UploadedFile[] = []
  landingOptions: LandingPageOptions
  isDeleting = false
  deleteFileId: number
  deleteFileIndex: number
  deleteFileName: string
  private _destroy$ = new Subject()

  ngOnDestroy(): void {
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  constructor(
    private auth: AuthService,
    private landingService: EventLandingService,
    private renderer: Renderer2,
    private toastr: ToastrService,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.landingOptions = this.options
    this.uploadedFiles = this.landingOptions.downloads.files.filter((file) => file)
  }

  /**
   * Simulate a click to the file input
   */
  uploadFile(): void {
    // Guard condition to stop overlapping uploads
    if (this.isUploading) return
    // Add fileOver class to Upload Container
    this.renderer.addClass(this.uploadContainer.nativeElement, 'inputSelected')
    this.uploadInput.nativeElement.click()
  }

  /**
   * Start uploading file
   * @param event event object
   */
  startUploading(event) {
    // Guard condition to stop overlapping uploads
    if (this.isUploading) return

    this.isUploading = true
    let fileList: FileList | null
    // Remove fileOver class if already exists
    this.renderer.removeClass(this.uploadContainer.nativeElement, 'inputSelected')
    // Get files to upload
    if (!event?.dataTransfer) fileList = (event.currentTarget as HTMLInputElement).files
    else fileList = event?.dataTransfer.files

    const fileTypes = ['image/jpeg', 'image/png', 'application/pdf']
    this.files = Array.from(fileList)
      .map((file, index) => {
        // Check if file size exceeds 30MB (size in bytes)
        if (file.size > 30 * 1024 * 1024) {
          this.toastr.error('Error', 'File size exceeds the 30MB limit')
          this.enableUploading()
          return
        }
        // Check if file type is valid
        if (!fileTypes.includes(file.type)) {
          this.toastr.error('Error', 'File type is not valid')
          this.enableUploading()
          return
        }
        return { id: index + 1, progress: 0, file, state: 'primary', subscribtion: null }
      })
      .filter((file) => !!file)

    this.files.forEach((fileObj) => {
      const noExtensionDisplayName = fileObj.file.name.replace(/\.[^/.]+$/, '')
      const encodedName = encodeURIComponent(fileObj.file.name)
      fileObj.subscribtion = this.landingService
        .uploadFiles(this.auth.currentWorkspace.slug, this.event.slug, encodedName, fileObj.file, noExtensionDisplayName)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (res: any) => {
            fileObj.progress = isNaN(res.loaded) || isNaN(res.total) ? 0 : Math.round((100 * res.loaded) / res.total)
            if (res instanceof HttpResponse) {
              this.files = this.files.filter((file) => file.id !== fileObj.id)
              this.uploadedFiles.unshift(res.body[0])
              this.saveFileIntoLandingOptions()
            }
          },
          error: () => {
            fileObj.state = 'warn'
            this.toastr.error('Error', 'Failed to upload this file')
            this.enableUploading()
          },
          complete: () => this.enableUploading()
        })
    })
    // Clear the input value
    this.uploadInput.nativeElement.value = ''
  }

  /**
   * Save uploaded file into landing Options JSON and make sure that it's not repeated
   * @returns void
   */
  saveFileIntoLandingOptions(): void {
    this.landingOptions.downloads.files = this.uploadedFiles
    this.landingService.updateLandingOptionsLocally(this.landingOptions)
  }

  /**
   * Remove Uploaded file from landing Options
   * @params file_id number
   * @returns void
   */
  removeFileFromLandingOptions(file_id: number): void {
    this.landingOptions.downloads.files = this.landingOptions.downloads.files.filter((file) => file.id !== file_id)
    this.landingService.updateLandingOptionsLocally(this.landingOptions)
  }

  /**
   * Enable uploading again in case of uploaded file are less that 4 files
   * @returns void
   */
  enableUploading(): void {
    const stoppedUploading = this.files.every((file) => file.state === 'warn' && file.progress === 100)
    if (!this.files.length || stoppedUploading) this.isUploading = false
  }

  /**
   * This method is used to show a modal for deleting an item.
   *
   * @param {number} index - The index of the file to be deleted from the uploadedFiles array.
   * @param {number} file_id - The unique identifier of the file to be deleted.
   * @param {UploadedFile} uploadedFile - The actual file object that is to be deleted.
   *
   * @returns {void}
   */
  showDeleteModal(index: number, file_id: number, uploadedFile: UploadedFile): void {
    this.isDeleting = true
    this.deleteFileId = file_id
    this.deleteFileIndex = index
    this.deleteFileName = `${uploadedFile.display_name}.${uploadedFile.extension}`
  }

  /**
   * Delete uploaded file from library
   * @param id
   * @returns void
   */
  deleteUploadedFile(): void {
    this.uploadedFiles.splice(this.deleteFileIndex, 1)
    this.removeFileFromLandingOptions(this.deleteFileId)
    this.landingService
      .deleteFile(this.auth.currentWorkspace.slug, this.deleteFileId)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        error: () => this.toastr.error('Error', 'Failed to delete this file')
      })
    this.isDeleting = false
    this.enableUploading()
  }

  /**
   * Cancel Uploading process
   * @param file_id number
   * @returns void
   */
  cancelUpload(file_id: number): void {
    this.files.find((file) => file.id === file_id).subscribtion.unsubscribe()
    this.files = this.files.filter((file) => file.id !== file_id)
    this.enableUploading()
  }

  selectFileName(event: Event) {
    const fileName = event.target as HTMLSpanElement
    const range = document.createRange()
    range.selectNodeContents(fileName)
    const sel = window.getSelection()
    sel.removeAllRanges()
    sel.addRange(range)
  }

  /**
   * Edit file name
   */
  editFileName(event: Event, file: UploadedFile, index: number) {
    this.customOptions.startPosition = index + 1
    let newName: string
    newName = (event.target as HTMLSpanElement).innerText
    if (!newName) {
      newName = this.uploadedFiles[index].display_name
      this.uploadedFiles[index] = { ...this.uploadedFiles[index], display_name: newName }
    }
    this.landingService
      .updateFile(this.auth.currentWorkspace.slug, file.id, newName)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res: UploadedFile) => {
          this.uploadedFiles[index] = { ...this.uploadedFiles[index], display_name: res.display_name }
          this.landingOptions.downloads.files[index] = { ...res }
          this.saveFileIntoLandingOptions()
        },
        error: () => this.toastr.error('Error', 'Failed to save the edited file name')
      })
  }

  /**
   * Change uploadStatus for the upload container
   * @param event string
   * @returns void
   */
  changeStatus(event: string): void {
    this.uploadStatus = event
  }

  /**
   * Format help text by replacing \n with HTML line breaks
   * @returns string - Formatted help text with HTML line breaks
   */
  getFormattedHelpText(): string {
    return this.helpText.replace(/\n/g, '<br>')
  }
}
